import { useEffect } from 'react'

import { useLocalStorage } from './useStorage'

/** Pick en or sv from each `English || Swedish` pair, including mixed sentences. */
export const localizeBilingualText = (text: string, locale: string): string => {
  if (!text.includes('||')) return text

  const pick = (en: string, sv: string) =>
    (locale === 'sv' ? sv.trim() || en.trim() : en.trim() || sv.trim())

  let out = text.replace(
    /"([^"]*?)\s*\|\|\s*([^"]*?)"/g,
    (_, en: string, sv: string) => `"${pick(en, sv)}"`
  )
  out = out.replace(
    /\(([^()]*?)\s*\|\|\s*([^()]*?)\)/g,
    (_, en: string, sv: string) => `(${pick(en, sv)})`
  )
  out = out.replace(
    /(\b(?:with|to)\s+)([A-Za-zÅÄÖåäö][A-Za-zÅÄÖåäö0-9+./'-]*(?:\s+[A-Za-zÅÄÖåäö][A-Za-zÅÄÖåäö0-9+./'-]*){0,2})\s*\|\|\s*([^|"]+?)(?=[.)]|$)/gi,
    (_, prefix: string, en: string, sv: string) => `${prefix}${pick(en, sv)}`
  )

  if (!out.includes('||')) return out

  const parts = out.split('||').map((part) => part.trim())
  if (parts.length === 2) return pick(parts[0], parts[1])
  return out
}

export const useLocalization = () => {
  const [locale, setLocale] = useLocalStorage('locale', 'en')

  useEffect(() => {
    const lang = new Intl.NumberFormat().resolvedOptions().locale

    if (lang && !localStorage.getItem('locale')) {
      // Only set locale automatically if user hasn't manually set it
      if (lang.startsWith('sv')) {
        setLocale('sv')
      } else {
        setLocale('en')
      }
    }
  }, [])

  const t = (en: string | undefined, sv?: string) => {
    // Handle undefined or null values
    if (!en) {
      return sv || ''
    }

    // If only one parameter is provided, split by "||"
    if (!sv && en.includes('||')) {
      return localizeBilingualText(en, locale)
    }

    // If two parameters are provided, use them directly
    // Fall back to English if Swedish is not provided
    return locale === 'sv' ? sv || en : en
  }

  const setLanguage = (languageCode: 'en' | 'sv') => {
    setLocale(languageCode)
  }

  return { t, locale, setLanguage }
}
